import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  PhoneCall,
  Server,
  PhoneForwarded,
  ShieldCheck,
  Zap,
  Copy,
  Check,
  Radio,
  Sliders,
  Sparkles,
  ArrowRight,
  ExternalLink,
  Lock,
  Unlock,
  Building,
  Volume2,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';

export interface SipForwardingContact {
  id: string;
  name: string;
  position: string;
  number: string;
  whatsappPhone?: string;
  enabled?: boolean;
}

export interface SipCallNodeData {
  connectionMode?: 'peer_trunk' | 'grandstream_api' | 'sip_extension';
  pbxHost?: string;
  pbxPort?: number;
  pbxTransport?: 'udp' | 'tcp' | 'tls';
  sipUsername?: string;
  sipPassword?: string;
  inboundDidNumber?: string;
  authorizedPbxIp?: string;
  enableUcmApi?: boolean;
  ucmApiUrl?: string;
  ucmApiUser?: string;
  ucmApiPassword?: string;
  greetingPrompt?: string;
  primaryLanguage?: 'si_en' | 'si' | 'en';
  secretaryExtension?: string;
  secretaryDepartment?: string;
  secretaryPhone?: string;
  transferMethod?: 'sip_refer' | 'ucm_api' | 'dual_fallback';
  preTransferVoiceLine?: string;
  alertSecretaryViaWhatsApp?: boolean;
  forwardingDirectory?: SipForwardingContact[];
  isCustomizable?: boolean;
  totalCallsAnswered?: number;
  totalCallsTransferred?: number;
  [key: string]: unknown;
}

export const SipCallNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as SipCallNodeData;
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const { isTenantView, tenant, authToken, tenantApiKey, pulseState, openPortalData } = useTenantView();

  // Master Admin vs. Tenant Admin lock governance (Rule 16)
  const isCustomizable = nodeData.isCustomizable !== false;
  const isLocked = isTenantView && !isCustomizable;

  const effectiveTenantId = useMemo(() => {
    if (tenant?.id) return String(tenant.id);
    if (tenant?.tenantKey) return tenant.tenantKey;
    if (typeof window !== 'undefined') {
      const match = window.location.pathname.match(/\/tenant\/([^\/]+)/);
      if (match && match[1]) return match[1];
    }
    return 'YOUR_TENANT_ID';
  }, [tenant]);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const persistNodeDataDirectly = (field: keyof SipCallNodeData, value: any, allUpdates?: Partial<SipCallNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'SIP_CALL',
            data: payloadData,
            widgetData: payloadData,
            tenantId: effectiveTenantId !== 'YOUR_TENANT_ID' ? effectiveTenantId : undefined,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[SipCallNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof SipCallNodeData, value: any) => {
    if (isLocked) return;
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  const connectionMode = nodeData.connectionMode || 'peer_trunk';
  const secretaryExt = nodeData.secretaryExtension || '101';
  const preTransferVoice = nodeData.preTransferVoiceLine ?? 'මම ඔබව අපගේ ලේකම්වරිය වෙත සම්බන්ධ කරන්නම්, කරුණාකර රැඳී සිටින්න.';
  const alertViaWa = nodeData.alertSecretaryViaWhatsApp !== false;

  const rawContacts = nodeData.forwardingDirectory;
  const contacts: SipForwardingContact[] = useMemo(() => {
    if (Array.isArray(rawContacts) && rawContacts.length > 0) return rawContacts;
    return [
      { id: 'c1', name: 'Sara', position: 'Secretary', number: '011165554656', enabled: true },
      { id: 'c2', name: 'Damayanthi', position: 'Secretary', number: '001654646416', enabled: true },
    ];
  }, [rawContacts]);

  const handleUpdateContact = (idx: number, field: keyof SipForwardingContact, value: any) => {
    if (isLocked) return;
    const updated = [...contacts];
    updated[idx] = { ...updated[idx], [field]: value };
    handleChange('forwardingDirectory', updated);
  };

  const handleAddContact = () => {
    if (isLocked) return;
    const newContact: SipForwardingContact = {
      id: `c_${Date.now()}`,
      name: '',
      position: 'Secretary',
      number: '',
      enabled: true,
    };
    handleChange('forwardingDirectory', [...contacts, newContact]);
  };

  const handleRemoveContact = (idx: number) => {
    if (isLocked) return;
    const updated = contacts.filter((_, i) => i !== idx);
    handleChange('forwardingDirectory', updated);
  };

  return (
    <div
      className={`group relative rounded-2xl border transition-all duration-300 backdrop-blur-xl min-w-[340px] max-w-[380px] bg-gradient-to-b from-[#081B24]/98 via-[#06121A]/98 to-[#030A0F]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-cyan-400 ring-2 ring-cyan-400/50 shadow-[0_0_35px_rgba(6,182,212,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-cyan-500/40 hover:border-cyan-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(6,182,212,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Quick Jump (Rule 17.5) ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0 flex items-center gap-2 whitespace-nowrap">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('sip_call', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-cyan-950 via-slate-900 to-emerald-950 border-2 border-cyan-400 text-cyan-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(6,182,212,0.8)] hover:border-cyan-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open SIP Call Studio"
          >
            <Server size={13} className="text-cyan-400 animate-pulse" />
            <span>Open SIP Call Studio</span>
            <ArrowRight size={12} className="text-cyan-300" />
          </button>
        </div>
      )}

      {/* ── Left Side Inbound/Outbound Handles (100% Color Parity) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-sip-call"
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-left-2 top-1/2 -translate-y-1/2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(6,182,212,0.8)]"
        title="Luna Decision Core Audio Leg"
      />

      {/* ── Right Side Output Handles ── */}
      <Handle
        type="source"
        position={Position.Right}
        id="out-sip-call"
        style={{ top: '40%' }}
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(6,182,212,0.8)]"
        title="Inbound Telephone Call Trigger -> Luna Core Hub"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-transfer"
        style={{ top: '70%' }}
        className="!bg-indigo-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-150 z-20 shadow-[0_0_10px_rgba(99,102,241,0.8)]"
        title="Escalate / Transfer Call -> Call Transfer Node"
      />

      {/* ── Card Header ── */}
      <div className="p-4 border-b border-cyan-500/20 bg-gradient-to-r from-cyan-950/40 via-slate-900/60 to-slate-950/80 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.3)]">
            <PhoneCall size={18} className="animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-black text-white tracking-wide uppercase">
                Enterprise SIP & PBX Phone
              </h3>
              <span className="px-1.5 py-0.5 rounded text-[8.5px] font-mono font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                LIVE VOICE
              </span>
            </div>
            <p className="text-[10px] font-mono text-cyan-400/80">
              Grandstream UCM & SLT/Dialog Landlines
            </p>
          </div>
        </div>

        {/* Master Admin Lock Toggle */}
        {!isTenantView && (
          <button
            type="button"
            onClick={() => handleChange('isCustomizable', !isCustomizable)}
            className={`nodrag p-1.5 rounded-lg border transition-all cursor-pointer ${
              isCustomizable
                ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40 hover:bg-cyan-500/30'
                : 'bg-slate-800 text-slate-400 border-slate-700'
            }`}
            title={isCustomizable ? 'Client Can Customize: ENABLED' : 'Client Can Customize: LOCKED'}
          >
            {isCustomizable ? <Unlock size={12} /> : <Lock size={12} />}
          </button>
        )}
      </div>

      {/* ── Card Body ── */}
      <div className="p-4 space-y-3.5">
        {saveIndicator && (
          <div className="text-[9px] font-mono text-emerald-400 text-center bg-emerald-950/40 border border-emerald-500/20 rounded py-1">
            {saveIndicator}
          </div>
        )}

        {isLocked && (
          <div className="flex items-center gap-1.5 text-[9px] font-mono text-amber-300 bg-amber-950/40 border border-amber-500/20 px-2 py-1 rounded-lg">
            <Lock size={10} />
            <span>🔒 Locked by Admin Template. Read-only mode.</span>
          </div>
        )}

        {/* Connection Mode Selector */}
        <div>
          <label className="text-[9.5px] font-mono font-bold text-slate-300 block mb-1 uppercase tracking-wider">
            PBX Connection Mode:
          </label>
          <div className="grid grid-cols-3 gap-1">
            {[
              { id: 'peer_trunk', label: 'Peer Trunk', sub: 'Grandstream (Rec)' },
              { id: 'grandstream_api', label: 'UCM API', sub: 'HTTPS REST' },
              { id: 'sip_extension', label: 'Extension', sub: 'Client Agent' },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                disabled={isLocked}
                onClick={() => handleChange('connectionMode', m.id)}
                className={`nodrag py-1.5 px-1 rounded-lg text-center border transition-all cursor-pointer ${
                  connectionMode === m.id
                    ? 'bg-cyan-500/20 text-cyan-200 border-cyan-400 shadow-[0_0_12px_rgba(6,182,212,0.3)]'
                    : 'bg-slate-900/60 text-slate-400 border-white/5 hover:border-white/10'
                }`}
              >
                <span className="text-[9px] font-mono font-bold block">{m.label}</span>
                <span className="text-[7.5px] font-mono text-slate-400 block">{m.sub}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Office Telephone Line (DID) */}
        <div>
          <label className="text-[9.5px] font-mono font-bold text-slate-300 block mb-1">
            Business Landline / Phone Number:
          </label>
          <div className="relative">
            <input
              type="text"
              disabled={isLocked}
              value={nodeData.inboundDidNumber || ''}
              onChange={(e) => handleChange('inboundDidNumber', e.target.value)}
              placeholder="e.g. +94112345678 or 0112345678"
              className="nodrag w-full bg-slate-950/90 border border-cyan-500/30 rounded-lg px-2.5 py-1.5 text-[10px] font-mono text-white placeholder-slate-600 focus:outline-none focus:border-cyan-400"
            />
          </div>
        </div>

        {/* Grandstream PBX Host / Whitelist IP */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[9.5px] font-mono font-bold text-slate-300">
              Grandstream Public IP / Host:
            </label>
            <span className="text-[8px] font-mono text-cyan-400">Trunk Whitelist</span>
          </div>
          <input
            type="text"
            disabled={isLocked}
            value={nodeData.pbxHost || ''}
            onChange={(e) => handleChange('pbxHost', e.target.value)}
            placeholder="e.g. 124.43.x.x or ucm.businesseye.lk"
            className="nodrag w-full bg-slate-950/90 border border-white/10 rounded-lg px-2.5 py-1.5 text-[10px] font-mono text-white placeholder-slate-600 focus:outline-none focus:border-cyan-400"
          />
        </div>

        {/* ── Autonomous Secretary Transfer Section ── */}
        <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-950/40 via-slate-900/60 to-slate-950 border border-indigo-500/30 space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-indigo-300 font-bold text-[10px] uppercase tracking-wider">
              <PhoneForwarded size={13} className="text-indigo-400 animate-pulse" />
              <span>Secretary Call Transfer</span>
            </div>
            <span className="px-1.5 py-0.5 rounded text-[8px] font-mono bg-indigo-500/20 text-indigo-200 border border-indigo-500/30">
              SIP REFER
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[8.5px] font-mono text-slate-400 block mb-0.5">
                Secretary Extension:
              </label>
              <input
                type="text"
                disabled={isLocked}
                value={secretaryExt}
                onChange={(e) => handleChange('secretaryExtension', e.target.value)}
                placeholder="e.g. 101"
                className="nodrag w-full bg-slate-950 border border-indigo-500/30 rounded-lg px-2 py-1 text-[10px] font-mono text-indigo-200 focus:outline-none focus:border-indigo-400"
              />
            </div>
            <div>
              <label className="text-[8.5px] font-mono text-slate-400 block mb-0.5">
                Target Department:
              </label>
              <input
                type="text"
                disabled={isLocked}
                value={nodeData.secretaryDepartment || 'Secretary & Front Desk'}
                onChange={(e) => handleChange('secretaryDepartment', e.target.value)}
                placeholder="Secretary"
                className="nodrag w-full bg-slate-950 border border-white/10 rounded-lg px-2 py-1 text-[10px] font-mono text-white focus:outline-none focus:border-indigo-400"
              />
            </div>
          </div>

          {/* Pre-Transfer Voice Prompt */}
          <div>
            <label className="text-[8.5px] font-mono text-slate-400 block mb-0.5">
              Spoken Announcement Before Transfer:
            </label>
            <textarea
              rows={2}
              disabled={isLocked}
              value={preTransferVoice}
              onChange={(e) => handleChange('preTransferVoiceLine', e.target.value)}
              className="nodrag w-full bg-slate-950 border border-white/10 rounded-lg p-1.5 text-[9px] font-mono text-slate-200 focus:outline-none focus:border-indigo-400 resize-none"
            />
          </div>

          {/* WhatsApp Alert to Secretary */}
          <div className="flex items-center justify-between pt-1 border-t border-indigo-500/20">
            <span className="text-[8.5px] font-mono text-slate-400">
              Instant WhatsApp Ping to Secretary:
            </span>
            <input
              type="checkbox"
              disabled={isLocked}
              checked={alertViaWa}
              onChange={(e) => handleChange('alertSecretaryViaWhatsApp', e.target.checked)}
              className="nodrag w-3.5 h-3.5 rounded bg-slate-950 border-indigo-500 text-indigo-500 focus:ring-indigo-400/30 cursor-pointer"
            />
          </div>

          {/* ── Call Forwarding Numbers Directory ── */}
          <div className="pt-2 border-t border-indigo-500/20 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[8.5px] font-mono text-indigo-300 font-bold uppercase tracking-wider flex items-center gap-1">
                <Users size={11} className="text-indigo-400" />
                Forwarding Numbers Directory:
              </span>
              {!isLocked && (
                <button
                  type="button"
                  onClick={handleAddContact}
                  className="nodrag text-[8px] font-mono font-bold text-cyan-300 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/40 px-2 py-0.5 rounded flex items-center gap-1 transition cursor-pointer"
                >
                  <Plus size={10} />
                  <span>Add Line</span>
                </button>
              )}
            </div>

            <div className="space-y-1.5 max-h-[160px] overflow-y-auto custom-scrollbar pr-0.5">
              {contacts.map((c, idx) => (
                <div key={c.id || idx} className="p-1.5 rounded-lg bg-slate-950/80 border border-indigo-500/20 grid grid-cols-12 gap-1 items-center">
                  <div className="col-span-4">
                    <input
                      type="text"
                      disabled={isLocked}
                      value={c.name}
                      onChange={(e) => handleUpdateContact(idx, 'name', e.target.value)}
                      placeholder="Name (e.g. Sara)"
                      className="nodrag w-full bg-slate-900 border border-white/10 rounded px-1.5 py-0.5 text-[9px] font-mono text-white placeholder-slate-600 focus:outline-none focus:border-indigo-400"
                    />
                  </div>
                  <div className="col-span-3">
                    <input
                      type="text"
                      disabled={isLocked}
                      value={c.position}
                      onChange={(e) => handleUpdateContact(idx, 'position', e.target.value)}
                      placeholder="Position"
                      className="nodrag w-full bg-slate-900 border border-white/10 rounded px-1.5 py-0.5 text-[9px] font-mono text-indigo-300 placeholder-slate-600 focus:outline-none focus:border-indigo-400"
                    />
                  </div>
                  <div className="col-span-4">
                    <input
                      type="text"
                      disabled={isLocked}
                      value={c.number}
                      onChange={(e) => handleUpdateContact(idx, 'number', e.target.value)}
                      placeholder="011... or Ext"
                      className="nodrag w-full bg-slate-900 border border-white/10 rounded px-1.5 py-0.5 text-[9px] font-mono text-emerald-300 placeholder-slate-600 focus:outline-none focus:border-indigo-400"
                    />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    {!isLocked && (
                      <button
                        type="button"
                        onClick={() => handleRemoveContact(idx)}
                        className="nodrag text-slate-500 hover:text-rose-400 p-0.5 transition cursor-pointer"
                        title="Remove line"
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── 1-Click Copy Box for Grandstream Technician ── */}
        <div className="p-2.5 rounded-xl bg-slate-950/90 border border-cyan-500/20 space-y-1.5">
          <div className="flex items-center justify-between text-[8.5px] font-mono text-cyan-300 font-bold uppercase">
            <span>Grandstream Peer Trunk Settings:</span>
            <span className="text-[8px] text-slate-500">Port 5060 / 5061</span>
          </div>

          <div className="grid grid-cols-2 gap-1 text-[8px] font-mono text-slate-400">
            <div className="p-1 rounded bg-slate-900/80 border border-white/5 flex items-center justify-between">
              <span>Host: wa.kangavena.com</span>
              <button
                type="button"
                onClick={() => copyToClipboard('wa.kangavena.com', 'host')}
                className="text-cyan-400 hover:text-cyan-200"
              >
                {copiedField === 'host' ? <Check size={10} /> : <Copy size={10} />}
              </button>
            </div>
            <div className="p-1 rounded bg-slate-900/80 border border-white/5 flex items-center justify-between">
              <span>Port: 5060 (UDP)</span>
              <button
                type="button"
                onClick={() => copyToClipboard('5060', 'port')}
                className="text-cyan-400 hover:text-cyan-200"
              >
                {copiedField === 'port' ? <Check size={10} /> : <Copy size={10} />}
              </button>
            </div>
          </div>

          <div className="p-1 rounded bg-slate-900/80 border border-white/5 flex items-center justify-between text-[8px] font-mono text-slate-400">
            <span className="truncate">URI: sip:wa.kangavena.com:5060?tenant_id={effectiveTenantId}</span>
            <button
              type="button"
              onClick={() => copyToClipboard(`sip:wa.kangavena.com:5060?tenant_id=${effectiveTenantId}`, 'uri')}
              className="text-cyan-400 hover:text-cyan-200 shrink-0 ml-1"
            >
              {copiedField === 'uri' ? <Check size={10} /> : <Copy size={10} />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Footer / Status ── */}
      <div className="px-4 py-2 bg-slate-950/80 border-t border-cyan-500/20 rounded-b-2xl flex items-center justify-between text-[8.5px] font-mono text-slate-400">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
          <span>SIP Trunk: Ready</span>
        </span>
        <span className="text-cyan-400/90 font-bold">
          Codecs: PCMU / PCMA / Opus
        </span>
      </div>
    </div>
  );
});

SipCallNode.displayName = 'SipCallNode';
export default SipCallNode;
